import { MouseEvent } from "react";
import styled from "styled-components";
import { canAddCalendarConnection } from "@lifeat/shared/calendar/CalendarUtil";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { useCallToActionContext } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import OutlookLogo from "@resources/logos/integrations/Outlook/outlook_logo.png";
import { platform } from "@todesktop/client-core";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import {
    CalendarIntegrationEventSource,
    StartOutlookCalendarAuthEvent,
} from "../../../../features/calendar/AmplitudeCalendarEvents";

const StyledButton = styled(LifeAtButtonV2)`
    & .lifeat-button-content {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        height: auto;
    }
`;

const IconImg = styled.img`
    width: 20px;
    height: auto;
`;

interface OutlookCalendarAuthButtonProps {
    source: CalendarIntegrationEventSource;
    className?: string;
}
function OutlookCalendarAuthButton(props: OutlookCalendarAuthButtonProps) {
    const amplitudeService = AmplitudeService.getInstance();
    const { userData } = useUserContext();
    const { setUpgradeCtaContext } = useCallToActionContext();

    const url = calculateOutlookSignInUrl();

    const handleClick = async (e: MouseEvent) => {
        if (userData == null) {
            e.preventDefault();
            return;
        }

        if (!canAddCalendarConnection(userData)) {
            e.preventDefault();
            setUpgradeCtaContext("multipleCalendars", UpgradeCTASource.CALENDAR_AUTH_BUTTON);
            return;
        }

        amplitudeService.logEvent(new StartOutlookCalendarAuthEvent(props.source));
        if (window.todesktop) {
            e.preventDefault();
            await platform.os.openURL(url);
        }
    };

    return (
        <StyledButton
            className={props.className}
            variant="outline"
            href={calculateOutlookSignInUrl()}
            onClick={handleClick}
        >
            <IconImg alt={`outlook_logo`} src={OutlookLogo} />
            <span>Outlook</span>
        </StyledButton>
    );
}

export default OutlookCalendarAuthButton;

function calculateOutlookSignInUrl() {
    const SCOPES = [
        "openid",
        "profile",
        "email",
        "User.Read",
        "offline_access",
        "Calendars.ReadWrite",
        "Calendars.ReadWrite.Shared",
    ];

    const params = new URLSearchParams({
        response_type: "code",
        redirect_uri: new URL("/oauth/outlook-calendar/callback", window.location.origin).href,
        prompt: "select_account",
        state: JSON.stringify({ shouldGoBackToDesktop: !!window.todesktop }),
        client_id: process.env.MICROSOFT_CLIENT_ID,
        scope: SCOPES.join(" "),
    });

    return `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?${params}`;
}
